import React, { useState } from 'react';
import { toast } from 'react-toastify';
import styles from './InputForm.module.css';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { STATUS_OPTIONS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatCurrency';

const InputForm = ({ onCalculate }) => {
  const [formData, setFormData] = useState({
    nama: '',
    usia: '',
    status: 'menikah',
    jumlahAnak: '',
    usiaAnakTermuda: '',
    penghasilan: '',
    pengeluaranBulanan: '',
    danaPendidikan1: '',
    danaPendidikan2: '',
    danaPendidikan3: '',
    danaLainnya: '',
    tabungan: '',
    investasi: '',
    asuransiAda: '',
    properti: '',
    hutangKPR: '',
    hutangMobil: '',
    hutangKartuKredit: '',
    hutangLainnya: '',
    persentaseProteksi: 80,
    persentasePengeluaran: 70,
    lamaKebutuhan: 15
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.nama) newErrors.nama = 'Nama wajib diisi';
    if (!formData.usia) newErrors.usia = 'Usia wajib diisi';
    if (formData.usia < 18 || formData.usia > 70) newErrors.usia = 'Usia harus antara 18-70 tahun';
    if (!formData.penghasilan) newErrors.penghasilan = 'Penghasilan wajib diisi';
    if (!formData.pengeluaranBulanan) newErrors.pengeluaranBulanan = 'Pengeluaran wajib diisi';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Mohon lengkapi data dengan benar');
      return;
    }

    // Hitung total kebutuhan dana masa depan
    const danaMasaDepan = 
      (parseInt(formData.danaPendidikan1) || 0) +
      (parseInt(formData.danaPendidikan2) || 0) +
      (parseInt(formData.danaPendidikan3) || 0) +
      (parseInt(formData.danaLainnya) || 0);

    // Hitung total aset lancar
    const totalAset = 
      (parseInt(formData.tabungan) || 0) +
      (parseInt(formData.investasi) || 0) +
      (parseInt(formData.asuransiAda) || 0) +
      (parseInt(formData.properti) || 0);

    // Hitung total kewajiban
    const totalKewajiban = 
      (parseInt(formData.hutangKPR) || 0) +
      (parseInt(formData.hutangMobil) || 0) +
      (parseInt(formData.hutangKartuKredit) || 0) +
      (parseInt(formData.hutangLainnya) || 0);

    const calculationData = {
      ...formData,
      danaMasaDepan,
      totalAset,
      totalKewajiban,
      penghasilan: parseInt(formData.penghasilan) || 0,
      pengeluaranBulanan: parseInt(formData.pengeluaranBulanan) || 0,
      usia: parseInt(formData.usia) || 0,
      jumlahAnak: parseInt(formData.jumlahAnak) || 0,
      usiaAnakTermuda: parseInt(formData.usiaAnakTermuda) || 0,
      persentaseProteksi: parseInt(formData.persentaseProteksi) || 80,
      persentasePengeluaran: parseInt(formData.persentasePengeluaran) || 70,
      lamaKebutuhan: parseInt(formData.lamaKebutuhan) || 15
    };

    onCalculate(calculationData);
    toast.success('Perhitungan berhasil!');
  };

  return (
    <Card>
      <h2 className={styles.title}>📋 Form Data Keuangan</h2>
      <p className={styles.subtitle}>Isi data dengan jujur untuk hasil yang akurat</p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Data Pribadi</h3>
          <div className={styles.grid2}>
            <Input
              label="Nama Lengkap"
              value={formData.nama}
              onChange={(val) => handleChange('nama', val)}
              error={errors.nama}
              required
            />
            <Input
              label="Usia"
              type="number"
              value={formData.usia}
              onChange={(val) => handleChange('usia', val)}
              error={errors.usia}
              placeholder="18-70 tahun"
              required
            />
          </div>

          <div className={styles.grid2}>
            <div className={styles.selectGroup}>
              <label className={styles.label}>Status Pernikahan</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className={styles.select}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <Input
              label="Jumlah Anak"
              type="number"
              value={formData.jumlahAnak}
              onChange={(val) => handleChange('jumlahAnak', val)}
            />
          </div>

          <Input
            label="Usia Anak Termuda"
            type="number"
            value={formData.usiaAnakTermuda}
            onChange={(val) => handleChange('usiaAnakTermuda', val)}
            placeholder="Isi jika sudah punya anak"
          />
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Penghasilan & Pengeluaran</h3>
          <div className={styles.grid2}>
            <Input
              label="Penghasilan Bulanan Bersih"
              type="currency"
              value={formData.penghasilan}
              onChange={(val) => handleChange('penghasilan', val)}
              error={errors.penghasilan}
              required
            />
            <Input
              label="Total Pengeluaran Bulanan"
              type="currency"
              value={formData.pengeluaranBulanan}
              onChange={(val) => handleChange('pengeluaranBulanan', val)}
              error={errors.pengeluaranBulanan}
              required
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Dana Masa Depan</h3>
          <Input
            label="Dana Pendidikan Anak 1"
            type="currency"
            value={formData.danaPendidikan1}
            onChange={(val) => handleChange('danaPendidikan1', val)}
            placeholder="Total hingga S1"
          />
          <Input
            label="Dana Pendidikan Anak 2"
            type="currency"
            value={formData.danaPendidikan2}
            onChange={(val) => handleChange('danaPendidikan2', val)}
          />
          <Input
            label="Dana Pendidikan Anak 3"
            type="currency"
            value={formData.danaPendidikan3}
            onChange={(val) => handleChange('danaPendidikan3', val)}
          />
          <Input
            label="Dana Lainnya (pernikahan, usaha, dll)"
            type="currency"
            value={formData.danaLainnya}
            onChange={(val) => handleChange('danaLainnya', val)}
          />
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Aset Lancar</h3>
          <div className={styles.grid2}>
            <Input
              label="Tabungan & Deposito"
              type="currency"
              value={formData.tabungan}
              onChange={(val) => handleChange('tabungan', val)}
            />
            <Input
              label="Reksadana / Saham"
              type="currency"
              value={formData.investasi}
              onChange={(val) => handleChange('investasi', val)}
            />
            <Input
              label="Asuransi Jiwa yang Ada (UP)"
              type="currency"
              value={formData.asuransiAda}
              onChange={(val) => handleChange('asuransiAda', val)}
            />
            <Input
              label="Properti yang Bisa Dijual Cepat"
              type="currency"
              value={formData.properti}
              onChange={(val) => handleChange('properti', val)}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Kewajiban / Hutang</h3>
          <div className={styles.grid2}>
            <Input
              label="Sisa Hutang KPR"
              type="currency"
              value={formData.hutangKPR}
              onChange={(val) => handleChange('hutangKPR', val)}
            />
            <Input
              label="Sisa Hutang Kredit Mobil"
              type="currency"
              value={formData.hutangMobil}
              onChange={(val) => handleChange('hutangMobil', val)}
            />
            <Input
              label="Hutang Kartu Kredit"
              type="currency"
              value={formData.hutangKartuKredit}
              onChange={(val) => handleChange('hutangKartuKredit', val)}
            />
            <Input
              label="Hutang Lainnya"
              type="currency"
              value={formData.hutangLainnya}
              onChange={(val) => handleChange('hutangLainnya', val)}
            />
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Asumsi Perhitungan</h3>
          <div className={styles.grid3}>
            <Input
              label="Persentase Proteksi Penghasilan"
              type="number"
              value={formData.persentaseProteksi}
              onChange={(val) => handleChange('persentaseProteksi', val)}
              placeholder="80%"
            />
            <Input
              label="Persentase Pengeluaran (setelah tiada)"
              type="number"
              value={formData.persentasePengeluaran}
              onChange={(val) => handleChange('persentasePengeluaran', val)}
              placeholder="70%"
            />
            <Input
              label="Lama Kebutuhan (tahun)"
              type="number"
              value={formData.lamaKebutuhan}
              onChange={(val) => handleChange('lamaKebutuhan', val)}
              placeholder="15"
            />
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <Button type="submit" variant="primary" size="large" fullWidth>
            🚀 HITUNG KEBUTUHAN ASURANSI JIWA
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default InputForm;